import { createEffect } from 'solid-js';

/** Solid 2 has no onMount: an effect with an empty tracked compute runs once
 *  after first render, in the browser only. The effect function may return a cleanup. */
export const onMount = (fn: () => void | (() => void)) => createEffect(() => undefined, fn);

/** Reveal-on-scroll for `.reveal` elements rendered by the calling page.
 *  Mirrors the prototype script: adds `.in` when 12 % of the element is
 *  visible; everything is shown at once under prefers-reduced-motion. */
export function useReveal() {
  onMount(() => {
    const items = document.querySelectorAll<HTMLElement>('.reveal:not(.in)');
    if (!items.length) return;
    const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced || !('IntersectionObserver' in window)) {
      items.forEach((el) => el.classList.add('in'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.classList.add('in');
          io.unobserve(entry.target);
        }
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' },
    );
    items.forEach((el) => io.observe(el));
    return () => io.disconnect();
  });
}

/** Partner logo track: arrows appear on the side that still has cards, a click
 *  moves one full row; the wheel scrolls sideways over the track (page scroll
 *  resumes at either end); touch swipes natively. */
export function useLogoTrack() {
  onMount(() => {
    const offs: Array<() => void> = [];
    for (const scroller of document.querySelectorAll<HTMLElement>('.logos-scroller')) {
      const track = scroller.querySelector<HTMLElement>('.logos-track');
      const prev = scroller.querySelector<HTMLButtonElement>('.logos-arrow--prev');
      const next = scroller.querySelector<HTMLButtonElement>('.logos-arrow--next');
      if (!track || !prev || !next) continue;
      const update = () => {
        const max = track.scrollWidth - track.clientWidth;
        const canPrev = max > 1 && track.scrollLeft > 1;
        const canNext = max > 1 && track.scrollLeft < max - 1;
        prev.hidden = !canPrev;
        next.hidden = !canNext;
        scroller.toggleAttribute('data-can-prev', canPrev);
        scroller.toggleAttribute('data-can-next', canNext);
      };
      const page = (dir: 1 | -1) => track.scrollBy({ left: dir * track.clientWidth, behavior: 'smooth' });
      const onPrev = () => page(-1);
      const onNext = () => page(1);
      const onWheel = (e: WheelEvent) => {
        if (track.scrollWidth <= track.clientWidth) return;
        if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;
        const atStart = track.scrollLeft <= 0 && e.deltaY < 0;
        const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 1 && e.deltaY > 0;
        if (atStart || atEnd) return;
        e.preventDefault();
        track.scrollLeft += e.deltaY;
      };
      prev.addEventListener('click', onPrev);
      next.addEventListener('click', onNext);
      track.addEventListener('scroll', update, { passive: true });
      track.addEventListener('wheel', onWheel, { passive: false });
      const ro = new ResizeObserver(update);
      ro.observe(track);
      update();
      offs.push(() => {
        prev.removeEventListener('click', onPrev);
        next.removeEventListener('click', onNext);
        track.removeEventListener('scroll', update);
        track.removeEventListener('wheel', onWheel);
        ro.disconnect();
      });
    }
    return () => offs.forEach((off) => off());
  });
}
